import { useEffect, useState } from "react";
import Container from "react-bootstrap/Container";
import ntreeCoding from "./ntree_coding.png"; // Make sure the image is in the same folder or adjust the path

const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;
const API_URL = "/api/thank-you"; // Adjust if your API route is different

// Helper to base64 encode IP
function encodeBase64(str) {
    return window.btoa(unescape(encodeURIComponent(str)));
}

// Helper to format time remaining
function formatTimeRemaining(ms) {
    if (ms <= 0) return "Expired";
    const days = Math.floor(ms / (24 * 60 * 60 * 1000));
    const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
    return `${days}d ${hours}h ${minutes}m`;
}

function ThankYou() {
    const [showExpireBox, setShowExpireBox] = useState(true);
    const [expired, setExpired] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(null);

    useEffect(() => {
        // Set body overflow-y to auto for vertical scrolling
        document.body.style.overflowY = "auto";
        // Optional: clean up on unmount
        return () => {
            document.body.style.overflowY = "";
        };
    }, []);

    useEffect(() => {
        let interval;
        fetch("https://api.ipify.org?format=json")
            .then(res => res.json())
            .then(async data => {
                const ip = data.ip;
                const ipBase64 = encodeBase64(ip);
                // Check if IP exists and get create_time from DynamoDB via API
                const getRes = await fetch(`${API_URL}?ip=${ipBase64}`);
                if (getRes.ok) {
                    const info = await getRes.json();
                    const createTime = new Date(info.create_time).getTime();
                    const expireTime = createTime + ONE_MONTH_MS;
                    const updateTime = () => {
                        const now = Date.now();
                        const remaining = expireTime - now;
                        setTimeRemaining(remaining);
                        if (remaining <= 0) {
                            setExpired(true);
                            clearInterval(interval);
                        }
                    };
                    updateTime();
                    interval = setInterval(updateTime, 1000 * 60); // update every minute
                } else {
                    // If not found, persist IP via POST
                    await fetch(API_URL, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ ip })
                    });
                    setTimeRemaining(ONE_MONTH_MS);
                }
            })
            .catch(() => {
                // If IP fetch fails, do nothing (don't expire)
            });
        return () => interval && clearInterval(interval);
    }, []);

    if (expired) {
        return (
            <div
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #0f2027 0%, #2c5364 100%)",
                    position: "relative",
                    overflow: "hidden",
                    color: "#fff",
                    flexDirection: "column"
                }}
            >
                <h1 style={{ color: "#00ffe7", fontWeight: 700, textShadow: "0 0 12px #00ffe7" }}>This page has expired.</h1>
                <p style={{ color: "#b2ebf2", fontSize: "1.2rem" }}>
                    Thank you for visiting.<br />This page is no longer available.
                </p>
            </div>
        );
    }

    return (
        <div
            style={{
                minHeight: "100dvh", // Use dynamic viewport height for mobile
                width: "100vw",
                // display: "flex",
                // alignItems: "center",
                // justifyContent: "center",
                background: "linear-gradient(135deg, #0f2027 0%, #2c5364 100%)",
                position: "relative",
                overflowY: "auto", // Enable vertical scrolling
                overflowX: "hidden"
            }}
        >
            {/* Animated floating ntree_coding.png as a non-blocking background */}
            <img
                src={ntreeCoding}
                alt="N-Tree Coding"
                className="floating-ntree-bg"
                style={{
                    position: "fixed", // Changed from "absolute" to "fixed"
                    width: "320px",
                    maxWidth: "80vw",
                    left: "auto",
                    right: "auto",
                    top: "auto",
                    bottom: "auto",
                    inset: 0, // Allow floating anywhere in the viewport
                    margin: "auto",
                    zIndex: 0, // stays behind main content
                    pointerEvents: "none",
                    opacity: 0.55,
                    filter: "drop-shadow(0 8px 32px #00ffe7aa) blur(0.5px)"
                }}
                aria-hidden="true"
            />
            {/* AI-themed animated SVG background */}
            <div style={{
                position: "absolute",
                inset: 0,
                zIndex: 0,
                pointerEvents: "none"
            }}>
                <svg width="100%" height="100%">
                    <defs>
                        <linearGradient id="aiGlow" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#00ffe7" />
                            <stop offset="100%" stopColor="#0072ff" />
                        </linearGradient>
                        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="6" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>
                    <polyline points="100,200 300,100 500,200 700,100" stroke="url(#aiGlow)" strokeWidth="3" fill="none" filter="url(#glow)" opacity="0.4" />
                    <polyline points="200,400 400,300 600,400 800,300" stroke="url(#aiGlow)" strokeWidth="2" fill="none" filter="url(#glow)" opacity="0.3" />
                    <circle cx="50%" cy="50%" r="120" stroke="url(#aiGlow)" strokeWidth="2" fill="none" filter="url(#glow)" opacity="0.2" />
                </svg>
            </div>
            {/* Main content */}
            <Container
                style={{
                    marginTop: "2rem",
                    marginBottom: "8rem", // Extra space for the expiry box
                    // maxWidth: 480,
                    // background: "rgba(255,255,255,0.07)",
                    // borderRadius: "24px",
                    // boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
                    // padding: "3rem 2.5rem",
                    // textAlign: "center",
                    // color: "#e0f7fa",
                    // backdropFilter: "blur(8px)",
                    // border: "1.5px solid rgba(0,255,231,0.18)",
                    // position: "relative"
                }}
            >
                <div className="text-center">
                    <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>
                        🤖
                    </div>
                    <h1 style={{
                        fontWeight: 700,
                        letterSpacing: "2px",
                        marginBottom: "1.2rem",
                        color: "#00ffe7",
                        textShadow: "0 0 12px #00ffe7, 0 0 2px #0072ff"
                    }}>
                        Thank You!
                    </h1>
                </div>

                <p style={{
                    fontSize: "1.25rem",
                    color: "#e0f7fa",
                    marginBottom: "1.5rem"
                }}>
                    I wanted to give you a special thanks for making me at ease, and also providing a final solution at the end.
                    It's a quality that's not common amongst most interviewers. So, I really apprciate that! <br /><br />

                    Here's interesting story. As an explorer and adventurer, I explore different kinds of AI, including but not limited to:
                    <pre>
                        <code style={{ background: "rgba(175, 228, 245, 0.30)", borderRadius: "5px" }}>GPT-4.1</code>
                        <code className="ms-2" style={{ background: "rgba(175, 228, 245, 0.30)", borderRadius: "5px" }}>GPT-4o</code>
                        <code className="ms-2" style={{ background: "rgba(175, 228, 245, 0.30)", borderRadius: "5px" }}>Claude 3.5 Sonnet</code>
                        <code className="ms-2" style={{ background: "rgba(175, 228, 245, 0.30)", borderRadius: "5px" }}>o3-mini</code>
                    </pre>
                    For first few seconds, I tried to see how these LLMs would react to question given by the interviewer. They don't get it alwyas right, but they did for the question I was being asked by you.
                    However, I decide not to use it and chose to rely on my own analysis because I believe conscience and critical thinking are paramount.<br /><br />

                    <strong>Two strengths I believe will make up for the areas where I fell short:</strong>
                    <ul style={{ textAlign: "left", margin: "1rem auto 1rem 2rem", color: "#b2ebf2" }}>
                        <li>
                            <span style={{ color: "#00ffe7", fontWeight: 500 }}>I know how to leverage AI effectively:</span>
                            <br />
                            I’m skilled at integrating AI tools to accelerate research, generate ideas, and streamline development.
                        </li>
                        <li style={{ marginTop: "0.5rem" }}>
                            <span style={{ color: "#00ffe7", fontWeight: 500 }}>I bring honesty and ethics to every engagement:</span>
                            <br />
                            Integrity is at the core of my work. I communicate transparently, own my mistakes, and act in the best interest of the team, the product, and the customer.
                        </li>
                    </ul>
                    And last but not least, I can sometimes be <b>creative</b> and <b>innovative</b> when it comes to solving a problem like one you see now. <br /><br />
                    LinkedIn doesn't allow me to send a message that are more than 200 characters. But I feel like I have ability to solve this problem and convey all I wanted to say, by means of sharing a link where user(s) can view the message that are more than 200 characters!

                    Well, if this round still don't work, I still wish the best for you and the company, and I hope at least we can stay connected via LinkedIn.<br /><br />
                    <span style={{ color: "#00ffe7", fontWeight: 500 }}>
                        Wishing you continued success on your journey!
                    </span>
                </p>
                <div style={{
                    fontSize: "1rem",
                    color: "#b2ebf2",
                    opacity: 0.7
                }}>
                    — Justin Kim | Backend (Java) | Frontend (Angular, React, Responsive Design)
                </div>
                {/* Dismissible Expiry Box */}
                {showExpireBox && (
                    <div
                        style={{
                            position: "fixed",
                            left: "50%",
                            bottom: "1.5rem",
                            transform: "translateX(-50%)",
                            width: "90%",
                            maxWidth: 480,
                            background: "rgba(0,0,0,0.82)",
                            color: "#fff",
                            borderRadius: "12px",
                            boxShadow: "0 2px 12px #00ffe755",
                            padding: "1rem 1.5rem",
                            fontSize: "1rem",
                            zIndex: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between"
                        }}
                    >
                        <span>
                            This web page will expire in 1 month from your first visit.
                            {
                                typeof timeRemaining === "number" && timeRemaining > 0 && (
                                    <span style={{ marginLeft: 12, color: "#00ffe7", fontWeight: 500 }}>
                                        (Time remaining: {formatTimeRemaining(timeRemaining)})
                                    </span>
                                )
                            }
                        </span>
                        <button
                            onClick={() => setShowExpireBox(false)}
                            style={{
                                marginLeft: "1.5rem",
                                background: "none",
                                border: "none",
                                color: "#00ffe7",
                                fontWeight: 700,
                                fontSize: "1.2rem",
                                cursor: "pointer"
                            }}
                            aria-label="Dismiss"
                        >
                            ×
                        </button>
                    </div>
                )}
            </Container>
            {/* Floating animation style */}
            <style>{`
                .floating-ntree-bg {
                    animation: float-ntree-bg 42s ease-in-out infinite alternate;
                }
                @keyframes float-ntree-bg {
                    0% {
                        transform: translate(0vw, 0vh) scale(1) rotate(-2deg);
                        opacity: 0.55;
                    }
                    25% {
                        transform: translate(30vw, 10vh) scale(1.03) rotate(2deg);
                        opacity: 0.65;
                    }
                    50% {
                        transform: translate(-10vw, 30vh) scale(1.04) rotate(-2deg);
                        opacity: 0.75;
                    }
                    75% {
                        transform: translate(20vw, -10vh) scale(1.02) rotate(2deg);
                        opacity: 0.65;
                    }
                    100% {
                        transform: translate(0vw, 0vh) scale(1) rotate(2deg);
                        opacity: 0.55;
                    }
                }
        `}</style>
        </div>
    );
}

export default ThankYou;